import { DeviceManagementConfigurationSetting } from '@microsoft/microsoft-graph-types-beta';
import {
    CHOICE_SETTING_INSTANCE_ODATA_TYPE,
    INTEGER_SETTING_VALUE_ODATA_TYPE,
    LAPS_CHILD_VALUE_TEMPLATE_REFS,
    LAPS_SETTING_IDS,
    LAPS_TEMPLATE_REFS,
    LapsSettingInstanceSchema,
    LapsSettingsForm,
    LapsSettingsSchema,
    SIMPLE_SETTING_INSTANCE_ODATA_TYPE,
    STRING_SETTING_VALUE_ODATA_TYPE,
} from './model';

/** Current (synced) value of every LAPS setting, or null when the policy does not configure it. */
export interface LapsCurrentSettings {
    backupDirectory: number | null;
    passwordAgeDays: number | null;
    passwordLength: number | null;
    passwordComplexity: number | null;
    administratorAccountName: string | null;
    postAuthenticationActions: number | null;
    postAuthenticationResetDelay: number | null;
    automaticAccountManagementEnabled: 'enabled' | 'disabled' | null;
    automaticAccountManagementTarget: number | null;
    automaticAccountManagementRandomizeName: 'enabled' | 'disabled' | null;
    automaticAccountManagementEnableAccount: 'enabled' | 'disabled' | null;
    automaticAccountManagementNameOrPrefix: string | null;
    passwordExpirationProtectionEnabled: 'enabled' | 'disabled' | null;
    adPasswordEncryptionEnabled: 'enabled' | 'disabled' | null;
    adPasswordEncryptionPrincipal: string | null;
    adEncryptedPasswordHistorySize: number | null;
}

// LAPS boolean settings (e.g. passwordexpirationprotectionenabled, adpasswordencryptionenabled) are
// modelled in the Settings Catalog as choice settings whose option values use `_true`/`_false`
// suffixes — NOT the `_1`/`_0` used by integer-backed choices (backupdirectory/complexity/postauth).
const enabledChoiceValue = (definitionId: string, enabled: boolean): string =>
    `${definitionId}_${enabled ? 'true' : 'false'}`;

const choiceValue = (definitionId: string, suffix: number): string => `${definitionId}_${suffix}`;

const isEnabledValue = (value: string | undefined): boolean => !!value && value.endsWith('_true');

/** Extract the trailing numeric suffix from a choice setting value (e.g. "..._backupdirectory_2" -> 2). */
const parseChoiceSuffix = (value: string | undefined): number | undefined => {
    if (!value) return undefined;
    const match = value.match(/_(\d+)$/);
    return match ? Number(match[1]) : undefined;
};

// ──────────────────────────────────────────────────────────────────────────
// Schema builders (form -> JSON schema)
// ──────────────────────────────────────────────────────────────────────────

/**
 * "Not configured" node. Instead of dropping a setting from the schema, we keep the entry (identity via
 * `const`) but set `settingDefinitionId.type` to `'null'`. That makes it a valid JSON Schema node that never
 * matches a real Graph setting (whose settingDefinitionId is a string), so it is inert to Ajv validation —
 * absence is enforced by the array minItems/maxItems, which count only configured settings. Both the form
 * reader and the Graph remediation builder skip these nodes.
 */
const notConfiguredSchema = (definitionId: string): LapsSettingInstanceSchema => ({
    properties: {
        settingDefinitionId: { type: 'null', const: definitionId },
    },
});

/** True when a schema node is "not configured" (`settingDefinitionId.type: 'null'`) — asserted absent from the policy. */
const isNotConfiguredNode = (node: LapsSettingInstanceSchema | undefined): boolean =>
    node?.properties?.settingDefinitionId?.type === 'null';

/** Count only real (sendable) instances; "not configured" markers do not occupy a slot in the policy. */
const configuredCount = (instances: LapsSettingInstanceSchema[]): number =>
    instances.filter((instance) => !isNotConfiguredNode(instance)).length;

const choiceSchema = (
    definitionId: string,
    value: string,
    children?: LapsSettingInstanceSchema[],
): LapsSettingInstanceSchema => {
    const node: LapsSettingInstanceSchema = {
        properties: {
            '@odata.type': { type: 'string', const: CHOICE_SETTING_INSTANCE_ODATA_TYPE },
            settingDefinitionId: { type: 'string', const: definitionId },
            choiceSettingValue: {
                type: 'object',
                properties: {
                    value: { type: 'string', const: value },
                },
            },
        },
    };

    if (children && children.length > 0) {
        // Markers stay in `oneOf` (self-describing) but are excluded from the count so the actual policy's
        // real children must match exactly the configured ones — a present-but-not-configured child deviates.
        const count = configuredCount(children);
        node.properties.choiceSettingValue!.properties.children = {
            type: 'array',
            minItems: count,
            maxItems: count,
            items: { oneOf: children },
        };
    }

    return node;
};

const simpleIntSchema = (definitionId: string, value: number): LapsSettingInstanceSchema => ({
    properties: {
        '@odata.type': { type: 'string', const: SIMPLE_SETTING_INSTANCE_ODATA_TYPE },
        settingDefinitionId: { type: 'string', const: definitionId },
        simpleSettingValue: {
            type: 'object',
            properties: {
                '@odata.type': { type: 'string', const: INTEGER_SETTING_VALUE_ODATA_TYPE },
                value: { type: 'number', const: Number(value) },
            },
        },
    },
});

const simpleStringSchema = (definitionId: string, value: string): LapsSettingInstanceSchema => ({
    properties: {
        '@odata.type': { type: 'string', const: SIMPLE_SETTING_INSTANCE_ODATA_TYPE },
        settingDefinitionId: { type: 'string', const: definitionId },
        simpleSettingValue: {
            type: 'object',
            properties: {
                '@odata.type': { type: 'string', const: STRING_SETTING_VALUE_ODATA_TYPE },
                value: { type: 'string', const: value },
            },
        },
    },
});

/** Build the desired LAPS settings JSON schema from the editable form value. */
export function buildLapsSettingsSchema(settings: LapsSettingsForm): LapsSettingsSchema {
    const instances: LapsSettingInstanceSchema[] = [];

    // Directory-specific children sit under the backup-directory choice. A "not configured" child is kept
    // as a marker (asserting absence) rather than dropped, so the schema stays complete and stable.
    const backupChildren: LapsSettingInstanceSchema[] = [];
    if (Number(settings.backupDirectory) === 1) {
        backupChildren.push(
            settings.configurePasswordAge
                ? simpleIntSchema(LAPS_SETTING_IDS.passwordAgeDaysAad, Number(settings.passwordAgeDays))
                : notConfiguredSchema(LAPS_SETTING_IDS.passwordAgeDaysAad),
        );
    } else if (Number(settings.backupDirectory) === 2) {
        backupChildren.push(
            settings.configurePasswordAge
                ? simpleIntSchema(LAPS_SETTING_IDS.passwordAgeDaysAd, Number(settings.passwordAgeDays))
                : notConfiguredSchema(LAPS_SETTING_IDS.passwordAgeDaysAd),
        );
        backupChildren.push(
            choiceSchema(
                LAPS_SETTING_IDS.passwordExpirationProtectionEnabled,
                enabledChoiceValue(
                    LAPS_SETTING_IDS.passwordExpirationProtectionEnabled,
                    settings.passwordExpirationProtectionEnabled === 'enabled',
                ),
            ),
        );
        backupChildren.push(
            choiceSchema(
                LAPS_SETTING_IDS.adPasswordEncryptionEnabled,
                enabledChoiceValue(
                    LAPS_SETTING_IDS.adPasswordEncryptionEnabled,
                    settings.adPasswordEncryptionEnabled === 'enabled',
                ),
            ),
        );
        backupChildren.push(
            simpleStringSchema(LAPS_SETTING_IDS.adPasswordEncryptionPrincipal, settings.adPasswordEncryptionPrincipal),
        );
        backupChildren.push(
            settings.configureAdEncryptedPasswordHistorySize
                ? simpleIntSchema(
                      LAPS_SETTING_IDS.adEncryptedPasswordHistorySize,
                      Number(settings.adEncryptedPasswordHistorySize),
                  )
                : notConfiguredSchema(LAPS_SETTING_IDS.adEncryptedPasswordHistorySize),
        );
    }

    instances.push(
        choiceSchema(
            LAPS_SETTING_IDS.backupDirectory,
            choiceValue(LAPS_SETTING_IDS.backupDirectory, Number(settings.backupDirectory)),
            backupChildren.length > 0 ? backupChildren : undefined,
        ),
    );

    // These are independent top-level settings (not children of the backup directory), so they are
    // always emitted regardless of the selected backup directory.
    instances.push(
        choiceSchema(
            LAPS_SETTING_IDS.passwordComplexity,
            choiceValue(LAPS_SETTING_IDS.passwordComplexity, Number(settings.passwordComplexity)),
        ),
    );
    instances.push(
        settings.configurePasswordLength
            ? simpleIntSchema(LAPS_SETTING_IDS.passwordLength, Number(settings.passwordLength))
            : notConfiguredSchema(LAPS_SETTING_IDS.passwordLength),
    );
    instances.push(
        settings.postAuthenticationActions !== 'notConfigured'
            ? choiceSchema(
                  LAPS_SETTING_IDS.postAuthenticationActions,
                  choiceValue(LAPS_SETTING_IDS.postAuthenticationActions, Number(settings.postAuthenticationActions)),
              )
            : notConfiguredSchema(LAPS_SETTING_IDS.postAuthenticationActions),
    );
    instances.push(
        settings.configurePostAuthenticationResetDelay
            ? simpleIntSchema(
                  LAPS_SETTING_IDS.postAuthenticationResetDelay,
                  Number(settings.postAuthenticationResetDelay),
              )
            : notConfiguredSchema(LAPS_SETTING_IDS.postAuthenticationResetDelay),
    );
    instances.push(
        settings.administratorAccountName
            ? simpleStringSchema(LAPS_SETTING_IDS.administratorAccountName, settings.administratorAccountName)
            : notConfiguredSchema(LAPS_SETTING_IDS.administratorAccountName),
    );
    // Automatic account management (Windows 11 24H2+) is NOT part of Account Protection template
    // version _1, so a templated policy rejects it ("TemplateReference not found"). Only emit it once
    // its template reference GUIDs are registered in LAPS_TEMPLATE_REFS (from a real portal export) —
    // at which point the policy template id must also be the version that includes this setting.
    if (LAPS_TEMPLATE_REFS[LAPS_SETTING_IDS.automaticAccountManagementEnabled]) {
        if (settings.automaticAccountManagementEnabled === 'notConfigured') {
            instances.push(notConfiguredSchema(LAPS_SETTING_IDS.automaticAccountManagementEnabled));
        } else {
            // When automatic account management is enabled it carries its own child settings; each
            // "not configured" child is kept as a marker (asserting absence) rather than dropped.
            const autoAccountChildren: LapsSettingInstanceSchema[] = [];
            if (settings.automaticAccountManagementEnabled === 'enabled') {
                autoAccountChildren.push(
                    settings.automaticAccountManagementTarget !== 'notConfigured'
                        ? choiceSchema(
                              LAPS_SETTING_IDS.automaticAccountManagementTarget,
                              choiceValue(
                                  LAPS_SETTING_IDS.automaticAccountManagementTarget,
                                  Number(settings.automaticAccountManagementTarget),
                              ),
                          )
                        : notConfiguredSchema(LAPS_SETTING_IDS.automaticAccountManagementTarget),
                );
                autoAccountChildren.push(
                    settings.automaticAccountManagementRandomizeName !== 'notConfigured'
                        ? choiceSchema(
                              LAPS_SETTING_IDS.automaticAccountManagementRandomizeName,
                              enabledChoiceValue(
                                  LAPS_SETTING_IDS.automaticAccountManagementRandomizeName,
                                  settings.automaticAccountManagementRandomizeName === 'enabled',
                              ),
                          )
                        : notConfiguredSchema(LAPS_SETTING_IDS.automaticAccountManagementRandomizeName),
                );
                autoAccountChildren.push(
                    settings.automaticAccountManagementEnableAccount !== 'notConfigured'
                        ? choiceSchema(
                              LAPS_SETTING_IDS.automaticAccountManagementEnableAccount,
                              enabledChoiceValue(
                                  LAPS_SETTING_IDS.automaticAccountManagementEnableAccount,
                                  settings.automaticAccountManagementEnableAccount === 'enabled',
                              ),
                          )
                        : notConfiguredSchema(LAPS_SETTING_IDS.automaticAccountManagementEnableAccount),
                );
                autoAccountChildren.push(
                    settings.automaticAccountManagementNameOrPrefix
                        ? simpleStringSchema(
                              LAPS_SETTING_IDS.automaticAccountManagementNameOrPrefix,
                              settings.automaticAccountManagementNameOrPrefix,
                          )
                        : notConfiguredSchema(LAPS_SETTING_IDS.automaticAccountManagementNameOrPrefix),
                );
            }

            instances.push(
                choiceSchema(
                    LAPS_SETTING_IDS.automaticAccountManagementEnabled,
                    enabledChoiceValue(
                        LAPS_SETTING_IDS.automaticAccountManagementEnabled,
                        settings.automaticAccountManagementEnabled === 'enabled',
                    ),
                    autoAccountChildren.length > 0 ? autoAccountChildren : undefined,
                ),
            );
        }
    }

    // Markers stay in `oneOf` (self-describing) but are excluded from the count, so the actual policy must
    // contain exactly the real (configured) settings — a present-but-not-configured setting deviates.
    const realCount = configuredCount(instances);
    return {
        type: 'array',
        minItems: realCount,
        maxItems: realCount,
        items: {
            type: 'object',
            properties: {
                settingInstance: {
                    type: 'object',
                    oneOf: instances,
                },
            },
        },
    };
}

// ──────────────────────────────────────────────────────────────────────────
// Schema readers (JSON schema -> form)
// ──────────────────────────────────────────────────────────────────────────

// The readers skip "not configured" markers so a marked setting is read back as absent (i.e. not configured),
// keeping the existing "presence => configured" logic below unchanged.
const findTopLevel = (
    settingsSchema: LapsSettingsSchema,
    definitionId: string,
): LapsSettingInstanceSchema | undefined =>
    settingsSchema?.items?.properties?.settingInstance?.oneOf?.find(
        (item) => item?.properties?.settingDefinitionId?.const === definitionId && !isNotConfiguredNode(item),
    );

const backupChildrenOf = (settingsSchema: LapsSettingsSchema): LapsSettingInstanceSchema[] =>
    findTopLevel(settingsSchema, LAPS_SETTING_IDS.backupDirectory)?.properties?.choiceSettingValue?.properties?.children
        ?.items?.oneOf ?? [];

const findChild = (
    children: LapsSettingInstanceSchema[],
    definitionId: string,
): LapsSettingInstanceSchema | undefined =>
    children.find(
        (child) => child?.properties?.settingDefinitionId?.const === definitionId && !isNotConfiguredNode(child),
    );

const simpleNumberOf = (node: LapsSettingInstanceSchema | undefined): number | undefined => {
    const value = node?.properties?.simpleSettingValue?.properties?.value?.const;
    return typeof value === 'number' ? value : undefined;
};

const simpleStringOf = (node: LapsSettingInstanceSchema | undefined): string | undefined => {
    const value = node?.properties?.simpleSettingValue?.properties?.value?.const;
    return typeof value === 'string' ? value : undefined;
};

/** Read the editable form values out of a LAPS settings JSON schema. */
export function readLapsSettingsFromSchema(settingsSchema: LapsSettingsSchema): LapsSettingsForm {
    const backup = findTopLevel(settingsSchema, LAPS_SETTING_IDS.backupDirectory);
    const backupDirectory = parseChoiceSuffix(backup?.properties?.choiceSettingValue?.properties?.value?.const) ?? 0;

    const children = backupChildrenOf(settingsSchema);
    const ageChild =
        findChild(children, LAPS_SETTING_IDS.passwordAgeDaysAad) ??
        findChild(children, LAPS_SETTING_IDS.passwordAgeDaysAd);

    const complexity = findTopLevel(settingsSchema, LAPS_SETTING_IDS.passwordComplexity);
    const length = findTopLevel(settingsSchema, LAPS_SETTING_IDS.passwordLength);
    const adminName = findTopLevel(settingsSchema, LAPS_SETTING_IDS.administratorAccountName);
    const postActions = findTopLevel(settingsSchema, LAPS_SETTING_IDS.postAuthenticationActions);
    const postDelay = findTopLevel(settingsSchema, LAPS_SETTING_IDS.postAuthenticationResetDelay);

    const autoAccount = findTopLevel(settingsSchema, LAPS_SETTING_IDS.automaticAccountManagementEnabled);
    const autoAccountChildren = autoAccount?.properties?.choiceSettingValue?.properties?.children?.items?.oneOf ?? [];
    const autoTarget = findChild(autoAccountChildren, LAPS_SETTING_IDS.automaticAccountManagementTarget);
    const autoRandomize = findChild(autoAccountChildren, LAPS_SETTING_IDS.automaticAccountManagementRandomizeName);
    const autoEnableAccount = findChild(autoAccountChildren, LAPS_SETTING_IDS.automaticAccountManagementEnableAccount);
    const autoNameOrPrefix = findChild(autoAccountChildren, LAPS_SETTING_IDS.automaticAccountManagementNameOrPrefix);

    const expirationProtection = findChild(children, LAPS_SETTING_IDS.passwordExpirationProtectionEnabled);
    const adEncryption = findChild(children, LAPS_SETTING_IDS.adPasswordEncryptionEnabled);
    const adPrincipal = findChild(children, LAPS_SETTING_IDS.adPasswordEncryptionPrincipal);
    const adHistory = findChild(children, LAPS_SETTING_IDS.adEncryptedPasswordHistorySize);

    return {
        backupDirectory,
        configurePasswordAge: !!ageChild,
        passwordAgeDays: simpleNumberOf(ageChild) ?? 30,
        configurePasswordLength: !!length,
        passwordLength: simpleNumberOf(length) ?? 14,
        passwordComplexity:
            parseChoiceSuffix(complexity?.properties?.choiceSettingValue?.properties?.value?.const) ?? 4,
        administratorAccountName: simpleStringOf(adminName) ?? '',
        postAuthenticationActions: postActions
            ? (parseChoiceSuffix(postActions?.properties?.choiceSettingValue?.properties?.value?.const) ??
              'notConfigured')
            : 'notConfigured',
        configurePostAuthenticationResetDelay: !!postDelay,
        postAuthenticationResetDelay: simpleNumberOf(postDelay) ?? 24,
        automaticAccountManagementEnabled: autoAccount
            ? isEnabledValue(autoAccount?.properties?.choiceSettingValue?.properties?.value?.const)
                ? 'enabled'
                : 'disabled'
            : 'notConfigured',
        automaticAccountManagementTarget: autoTarget
            ? (parseChoiceSuffix(autoTarget?.properties?.choiceSettingValue?.properties?.value?.const) ?? 1)
            : 'notConfigured',
        automaticAccountManagementRandomizeName: autoRandomize
            ? isEnabledValue(autoRandomize?.properties?.choiceSettingValue?.properties?.value?.const)
                ? 'enabled'
                : 'disabled'
            : 'notConfigured',
        automaticAccountManagementEnableAccount: autoEnableAccount
            ? isEnabledValue(autoEnableAccount?.properties?.choiceSettingValue?.properties?.value?.const)
                ? 'enabled'
                : 'disabled'
            : 'notConfigured',
        automaticAccountManagementNameOrPrefix: simpleStringOf(autoNameOrPrefix) ?? '',
        passwordExpirationProtectionEnabled: expirationProtection
            ? isEnabledValue(expirationProtection?.properties?.choiceSettingValue?.properties?.value?.const)
                ? 'enabled'
                : 'disabled'
            : 'enabled',
        adPasswordEncryptionEnabled: adEncryption
            ? isEnabledValue(adEncryption?.properties?.choiceSettingValue?.properties?.value?.const)
                ? 'enabled'
                : 'disabled'
            : 'enabled',
        adPasswordEncryptionPrincipal: simpleStringOf(adPrincipal) ?? '',
        configureAdEncryptedPasswordHistorySize: !!adHistory,
        adEncryptedPasswordHistorySize: simpleNumberOf(adHistory) ?? 0,
    };
}

// ──────────────────────────────────────────────────────────────────────────
// Graph payload builder (JSON schema -> Microsoft Graph settings)
// ──────────────────────────────────────────────────────────────────────────

/**
 * Convert a single setting-instance schema node into the Microsoft Graph setting-instance shape.
 * Template references are attached only for top-level settings (`useTemplateRefs`); child settings
 * are emitted without references, matching the Account Protection template payload.
 */
const toGraphSettingInstance = (node: LapsSettingInstanceSchema, useTemplateRefs: boolean): any => {
    const settingDefinitionId = node.properties.settingDefinitionId.const;
    const ref = useTemplateRefs ? LAPS_TEMPLATE_REFS[settingDefinitionId] : undefined;

    // Top-level settings carry both an instance and value template reference (from LAPS_TEMPLATE_REFS).
    // Certain child settings carry a value-only reference (no instance reference) — see
    // LAPS_CHILD_VALUE_TEMPLATE_REFS (e.g. the automatic-account-management children).
    const instanceTemplateId = ref?.instance;
    const valueTemplateId =
        ref?.value ?? (useTemplateRefs ? undefined : LAPS_CHILD_VALUE_TEMPLATE_REFS[settingDefinitionId]);

    if (node.properties.choiceSettingValue) {
        const childSchemas = (node.properties.choiceSettingValue.properties.children?.items?.oneOf ?? []).filter(
            (child) => !isNotConfiguredNode(child),
        );
        const children = childSchemas.map((child) => toGraphSettingInstance(child, false));

        return {
            '@odata.type': CHOICE_SETTING_INSTANCE_ODATA_TYPE,
            settingDefinitionId,
            ...(instanceTemplateId
                ? { settingInstanceTemplateReference: { settingInstanceTemplateId: instanceTemplateId } }
                : {}),
            choiceSettingValue: {
                value: node.properties.choiceSettingValue.properties.value.const,
                ...(valueTemplateId
                    ? { settingValueTemplateReference: { settingValueTemplateId: valueTemplateId } }
                    : {}),
                children,
            },
        };
    }

    const valueOdataType = node.properties.simpleSettingValue!.properties['@odata.type'].const;

    return {
        '@odata.type': SIMPLE_SETTING_INSTANCE_ODATA_TYPE,
        settingDefinitionId,
        ...(instanceTemplateId
            ? { settingInstanceTemplateReference: { settingInstanceTemplateId: instanceTemplateId } }
            : {}),
        simpleSettingValue: {
            '@odata.type': valueOdataType,
            value: node.properties.simpleSettingValue!.properties.value.const,
            ...(valueTemplateId ? { settingValueTemplateReference: { settingValueTemplateId: valueTemplateId } } : {}),
        },
    };
};

/** Build the Microsoft Graph settings payload (used by the remediation action) from a LAPS settings schema. */
export function buildLapsGraphSettingsFromSchema(
    settingsSchema: LapsSettingsSchema,
): DeviceManagementConfigurationSetting[] {
    // "Not configured" markers describe no real setting, so they are never sent to Graph.
    const items = (settingsSchema?.items?.properties?.settingInstance?.oneOf ?? []).filter(
        (item) => !isNotConfiguredNode(item),
    );

    return items.map(
        (item) =>
            ({
                '@odata.type': '#microsoft.graph.deviceManagementConfigurationSetting',
                settingInstance: toGraphSettingInstance(item, true),
            }) as DeviceManagementConfigurationSetting,
    );
}

// ──────────────────────────────────────────────────────────────────────────
// Current-value readers (synced Graph data -> display values)
// ──────────────────────────────────────────────────────────────────────────

const findSettingFromData = (
    settings: DeviceManagementConfigurationSetting[],
    definitionId: string,
): DeviceManagementConfigurationSetting | undefined =>
    settings?.find((item) => item?.settingInstance?.settingDefinitionId === definitionId);

const backupChildrenFromData = (settings: DeviceManagementConfigurationSetting[]): any[] => {
    const parent = findSettingFromData(settings, LAPS_SETTING_IDS.backupDirectory);
    return ((parent?.settingInstance as any)?.choiceSettingValue?.children ?? []) as any[];
};

const findChildFromData = (children: any[], definitionId: string): any | undefined =>
    children.find((child) => child?.settingDefinitionId === definitionId);

/**
 * Flatten every setting-instance node in the policy — each top-level setting plus all of its
 * nested `choiceSettingValue.children`, at any depth.
 *
 * Intune's Settings Catalog syncs dependent settings inconsistently: the automatic
 * account-management children (target/randomizeName/enableAccount/nameOrPrefix) may arrive nested
 * under AutomaticAccountManagementEnabled, nested under Target, or as separate top-level settings.
 * Searching the fully-flattened tree by definition id locates them regardless of placement.
 */
const flattenSettingInstances = (settings: DeviceManagementConfigurationSetting[]): any[] => {
    const walk = (node: any): any[] => {
        if (!node) return [];
        const children = (node?.choiceSettingValue?.children ?? []) as any[];
        return [node, ...children.flatMap(walk)];
    };
    return (settings ?? []).flatMap((item) => walk((item as any)?.settingInstance));
};

const choiceSuffixFromData = (
    settings: DeviceManagementConfigurationSetting[],
    definitionId: string,
): number | null => {
    const setting = findSettingFromData(settings, definitionId);
    const value = (setting?.settingInstance as any)?.choiceSettingValue?.value as string | undefined;
    return parseChoiceSuffix(value) ?? null;
};

const simpleNumberFromData = (
    settings: DeviceManagementConfigurationSetting[],
    definitionId: string,
): number | null => {
    const setting = findSettingFromData(settings, definitionId);
    const value = (setting?.settingInstance as any)?.simpleSettingValue?.value;
    return typeof value === 'number' ? value : null;
};

const simpleStringFromData = (
    settings: DeviceManagementConfigurationSetting[],
    definitionId: string,
): string | null => {
    const setting = findSettingFromData(settings, definitionId);
    const value = (setting?.settingInstance as any)?.simpleSettingValue?.value;
    return typeof value === 'string' ? value : null;
};

/** Read the current (synced) value of every LAPS setting from the policy data. */
export function readLapsCurrentSettingsFromData(settings: DeviceManagementConfigurationSetting[]): LapsCurrentSettings {
    const children = backupChildrenFromData(settings);

    const ageChild =
        findChildFromData(children, LAPS_SETTING_IDS.passwordAgeDaysAad) ??
        findChildFromData(children, LAPS_SETTING_IDS.passwordAgeDaysAd);
    const ageValue = ageChild?.simpleSettingValue?.value;

    const expirationProtectionChild = findChildFromData(children, LAPS_SETTING_IDS.passwordExpirationProtectionEnabled);
    const adEncryptionChild = findChildFromData(children, LAPS_SETTING_IDS.adPasswordEncryptionEnabled);
    const adPrincipalChild = findChildFromData(children, LAPS_SETTING_IDS.adPasswordEncryptionPrincipal);
    const adHistoryChild = findChildFromData(children, LAPS_SETTING_IDS.adEncryptedPasswordHistorySize);

    const expirationProtectionValue = expirationProtectionChild?.choiceSettingValue?.value as string | undefined;
    const adEncryptionValue = adEncryptionChild?.choiceSettingValue?.value as string | undefined;

    const automaticAccountSetting = findSettingFromData(settings, LAPS_SETTING_IDS.automaticAccountManagementEnabled);
    const automaticAccountValue = (automaticAccountSetting?.settingInstance as any)?.choiceSettingValue?.value as
        | string
        | undefined;
    const autoAccountChildren = flattenSettingInstances(settings);
    const autoTargetChild = findChildFromData(autoAccountChildren, LAPS_SETTING_IDS.automaticAccountManagementTarget);
    const autoRandomizeChild = findChildFromData(
        autoAccountChildren,
        LAPS_SETTING_IDS.automaticAccountManagementRandomizeName,
    );
    const autoEnableAccountChild = findChildFromData(
        autoAccountChildren,
        LAPS_SETTING_IDS.automaticAccountManagementEnableAccount,
    );
    const autoNameOrPrefixChild = findChildFromData(
        autoAccountChildren,
        LAPS_SETTING_IDS.automaticAccountManagementNameOrPrefix,
    );
    const autoRandomizeValue = autoRandomizeChild?.choiceSettingValue?.value as string | undefined;
    const autoEnableAccountValue = autoEnableAccountChild?.choiceSettingValue?.value as string | undefined;

    return {
        backupDirectory: choiceSuffixFromData(settings, LAPS_SETTING_IDS.backupDirectory),
        passwordAgeDays: typeof ageValue === 'number' ? ageValue : null,
        passwordLength: simpleNumberFromData(settings, LAPS_SETTING_IDS.passwordLength),
        passwordComplexity: choiceSuffixFromData(settings, LAPS_SETTING_IDS.passwordComplexity),
        administratorAccountName: simpleStringFromData(settings, LAPS_SETTING_IDS.administratorAccountName),
        postAuthenticationActions: choiceSuffixFromData(settings, LAPS_SETTING_IDS.postAuthenticationActions),
        postAuthenticationResetDelay: simpleNumberFromData(settings, LAPS_SETTING_IDS.postAuthenticationResetDelay),
        automaticAccountManagementEnabled: automaticAccountValue
            ? isEnabledValue(automaticAccountValue)
                ? 'enabled'
                : 'disabled'
            : null,
        automaticAccountManagementTarget: parseChoiceSuffix(autoTargetChild?.choiceSettingValue?.value) ?? null,
        automaticAccountManagementRandomizeName: autoRandomizeValue
            ? isEnabledValue(autoRandomizeValue)
                ? 'enabled'
                : 'disabled'
            : null,
        automaticAccountManagementEnableAccount: autoEnableAccountValue
            ? isEnabledValue(autoEnableAccountValue)
                ? 'enabled'
                : 'disabled'
            : null,
        automaticAccountManagementNameOrPrefix:
            typeof autoNameOrPrefixChild?.simpleSettingValue?.value === 'string'
                ? autoNameOrPrefixChild.simpleSettingValue.value
                : null,
        passwordExpirationProtectionEnabled: expirationProtectionValue
            ? isEnabledValue(expirationProtectionValue)
                ? 'enabled'
                : 'disabled'
            : null,
        adPasswordEncryptionEnabled: adEncryptionValue
            ? isEnabledValue(adEncryptionValue)
                ? 'enabled'
                : 'disabled'
            : null,
        adPasswordEncryptionPrincipal:
            typeof adPrincipalChild?.simpleSettingValue?.value === 'string'
                ? adPrincipalChild.simpleSettingValue.value
                : null,
        adEncryptedPasswordHistorySize:
            typeof adHistoryChild?.simpleSettingValue?.value === 'number'
                ? adHistoryChild.simpleSettingValue.value
                : null,
    };
}
